import type { AppRouter } from '@foodlista/backend'
import type { OperationKey, OperationManifest } from '@foodlista/operations'
import type { OperationRun } from '@foodlista/trigger-vue'
import type { RouterClient } from '@orpc/server'
import type { QueryClient } from '@tanstack/vue-query'
import type {
  ScopedOperationAction,
  ScopedOperationEffect,
  ScopedOperationState,
} from '../utils/scoped-operation-reducer'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { useNow } from '@vueuse/core'
import { registerOperationCompletionEffect } from '../completion/registry'
import { OPERATIONS_CACHE_KEYS } from '../constants'
import {
  scopeCacheKey,
  setScopedOperationPending,
  setScopedOperationTracking,
  type TrackedOperation,
  useScopedOperationActive,
  useScopedOperationPending,
  useScopedOperationTracking,
} from '../scoped-operation-state'
import { PENDING_GRACE_MS } from '../utils/lifecycle'
import {
  buildScopedEnqueueInput,
  createScopeMatcher,
  type ScopedOperationScope,
  scopeFieldsFromContext,
  scopePayloadKeys,
} from '../utils/scoped-operation-contract'
import {
  findActiveScopedOperation,
  restaurantSlugFromScope,
  shouldHandleTerminalTransition,
  tokenAvailabilityError,
} from '../utils/scoped-operation-orchestration'
import {
  initialScopedOperationState,
  scopedOperationReducer,
} from '../utils/scoped-operation-reducer'
import { useRestaurantOperationsContext } from './provideRestaurantOperations'
import { useOperationActive } from './useOperationActive'

export interface DefineScopedOperationContext<
  Input,
  Scope extends ScopedOperationScope,
> {
  scope: Scope
  orpc: RouterClient<AppRouter>
  queryClient: QueryClient
  idempotencyKey: string
  buildEnqueueInput: (
    input: Input,
  ) => ReturnType<typeof buildScopedEnqueueInput<Input, Scope>>
}

export interface ScopedOperationCompletedContext<
  Result,
  Scope extends ScopedOperationScope,
> {
  result: Result
  scope: Partial<Scope>
  run: OperationRun
  queryClient: QueryClient
}

/**
 * Controls how concurrent calls with the same scope are deduplicated.
 *
 * Ask: **if the user fires the same scope twice in a row, do they want one job
 * or two?**
 *
 * - `{ mode: 'request' }` — every call is a fresh request. Spam-clicks
 *   produce multiple runs. Default. Use for "regenerate"-style actions where
 *   each click should produce its own run (e.g. allergen detection).
 *
 * - `{ mode: 'scope', keyFromScope }` — repeated calls with the same scope
 *   collapse into the single in-flight run. `keyFromScope` returns a stable
 *   string keyed off scope (only the scope-specific suffix;
 *   `type:slug:initiator:` is prefixed upstream). Returning `undefined` falls
 *   back to per-request.
 *   Use when collapsing duplicate triggers is the explicit UX goal (e.g.
 *   translating a menu to a target language — clicking twice while in-flight
 *   should be one job, not two).
 */
type ScopedOperationIdempotency<Scope extends ScopedOperationScope> =
  | { mode: 'request' }
  | {
    mode: 'scope'
    keyFromScope: (scope: Scope) => string | undefined
  }

export interface DefineScopedOperationDefinition<
  Input,
  Scope extends ScopedOperationScope,
  Result,
> {
  manifest: Pick<OperationManifest, 'type'>
  scopePayload: (scope: Scope) => Record<string, unknown>
  idempotency: ScopedOperationIdempotency<Scope>
  parseOutput: (run: OperationRun) => Result | Promise<Result>
  enqueue: (
    input: Input,
    ctx: DefineScopedOperationContext<Input, Scope>,
  ) => Promise<{ operationId: string, triggerRunId?: string }>
  onCompleted?: (
    ctx: ScopedOperationCompletedContext<Result, Scope>,
  ) => void | Promise<void>
  invalidateEnvelopes?: boolean | ((scope: Scope) => boolean)
}

export function defineScopedOperation<
  Input,
  Scope extends ScopedOperationScope,
  Result,
>(
  definition: DefineScopedOperationDefinition<Input, Scope, Result>,
) {
  const operationKey = definition.manifest.type as OperationKey
  const idempotencyKeyFromScope = definition.idempotency.mode === 'scope'
    ? definition.idempotency.keyFromScope
    : undefined
  const matchesScope = createScopeMatcher(
    operationKey,
    definition.scopePayload,
  )

  if (definition.onCompleted) {
    registerOperationCompletionEffect(
      operationKey,
      async ({
        run,
        queryClient,
        contextVersion,
        context,
        restaurantSlug,
        operationType,
      }) => {
        const result = await definition.parseOutput(run)
        const scope = scopeFieldsFromContext({
          type: operationType,
          contextVersion,
          context,
          restaurantSlug,
          keys: scopePayloadKeys(definition.scopePayload),
        }) as Partial<Scope>

        await definition.onCompleted?.({
          result,
          scope,
          run,
          queryClient,
        })
      },
    )
  }

  return function useDefinedScopedOperation(
    scope: MaybeRefOrGetter<Scope>,
    options: {
      onResolved?: (result: Result) => void
      onError?: (error: Error) => void
    } = {},
  ) {
    const { $orpc } = useNuxtApp()
    const queryClient = useQueryClient()
    const context = useRestaurantOperationsContext()
    const now = useNow({ interval: 10_000 })

    const resolvedScope = computed(() => toValue(scope))
    const cacheKey = computed(() =>
      scopeCacheKey(operationKey, resolvedScope.value))

    const trackingByScope = useScopedOperationTracking()
    const pendingByScope = useScopedOperationPending()
    const isActive = useScopedOperationActive(operationKey, scope)

    const tracking = computed(
      () => trackingByScope.value[cacheKey.value] ?? null,
    )

    const operationId = computed(() => tracking.value?.operationId ?? null)

    watch(
      operationId,
      (id, previousId) => {
        if (previousId) {
          context.removeWatchedOperation(previousId)
        }
        if (id) {
          context.addWatchedOperation(id)
        }
      },
      { immediate: true },
    )

    onScopeDispose(() => {
      const id = operationId.value
      if (id) {
        context.removeWatchedOperation(id)
      }
    })

    const { run, progress } = useOperationActive(operationId)

    const state = ref(initialScopedOperationState<Result>()) as Ref<
      ScopedOperationState<Result>
    >

    let asyncHandlers: {
      resolve: (value: Result) => void
      reject: (error: Error) => void
    } | null = null

    function setTracking(next: TrackedOperation | null) {
      setScopedOperationTracking(trackingByScope, cacheKey.value, next)
    }

    function applyEffect(effect: ScopedOperationEffect<Result>) {
      switch (effect.type) {
        case 'NOTIFY_RESOLVED':
          options.onResolved?.(effect.data)
          break
        case 'NOTIFY_ERROR':
          options.onError?.(effect.error)
          break
        case 'RESOLVE_ASYNC':
          asyncHandlers?.resolve(effect.data)
          asyncHandlers = null
          break
        case 'REJECT_ASYNC':
          asyncHandlers?.reject(effect.error)
          asyncHandlers = null
          break
        case 'START_ACTIVE':
          setScopedOperationPending(pendingByScope, cacheKey.value, true)
          break
        case 'SET_TRACKING':
          setTracking({ operationId: effect.operationId })
          break
        case 'STOP_ACTIVE':
          setScopedOperationPending(pendingByScope, cacheKey.value, false)
          setTracking(null)
          break
      }
    }

    function dispatch(action: ScopedOperationAction<Result>) {
      const { state: nextState, effects } = scopedOperationReducer(
        state.value,
        action,
      )
      state.value = nextState
      for (const effect of effects) {
        applyEffect(effect)
      }
    }

    function createClientIdempotencyKey() {
      const derived = idempotencyKeyFromScope?.(resolvedScope.value)
      if (derived) {
        return derived
      }

      return crypto.randomUUID()
    }

    async function enqueueWithDefinition(
      input: Input,
      enqueueOptions: { idempotencyKey: string },
    ): Promise<{ operationId: string, triggerRunId?: string }> {
      const buildEnqueueInput = (enqueueInput: Input) =>
        buildScopedEnqueueInput(
          enqueueInput,
          resolvedScope.value,
          definition.scopePayload,
          enqueueOptions.idempotencyKey,
        )

      const result = await definition.enqueue(input, {
        scope: resolvedScope.value,
        orpc: $orpc,
        queryClient,
        idempotencyKey: enqueueOptions.idempotencyKey,
        buildEnqueueInput,
      })

      const shouldInvalidate
        = definition.invalidateEnvelopes === undefined
        || definition.invalidateEnvelopes === true
        || (typeof definition.invalidateEnvelopes === 'function'
          && definition.invalidateEnvelopes(resolvedScope.value))

      if (shouldInvalidate) {
        const restaurantSlug = restaurantSlugFromScope(resolvedScope.value)
        if (restaurantSlug) {
          await queryClient.invalidateQueries({
            queryKey: OPERATIONS_CACHE_KEYS.envelopes(restaurantSlug),
          })
        }
      }

      return result
    }

    watch(
      () => [
        context.items.value,
        resolvedScope.value,
        operationId.value,
        pendingByScope.value[cacheKey.value],
      ] as const,
      ([items, currentScope, trackedId, isPending]) => {
        if (trackedId || isPending) {
          return
        }

        const match = findActiveScopedOperation(
          items,
          operationKey,
          currentScope,
          now.value,
          matchesScope,
        )
        if (!match) {
          return
        }

        dispatch({
          type: 'MATCHED_EXISTING_OPERATION',
          operationId: match.id,
          startedAt: match.createdAt,
        })
      },
      { immediate: true, deep: true },
    )

    watch(
      () => [operationId.value, run.value, state.value.startedAt, now.value] as const,
      ([trackedId, currentRun, started, currentNow]) => {
        if (!trackedId || currentRun || !started) {
          return
        }

        if (currentNow.getTime() - started.getTime() > PENDING_GRACE_MS) {
          dispatch({
            type: 'TIMEOUT_WAITING_FOR_RUN',
            error: new Error(`${operationKey} timed out waiting for run`),
          })
        }
      },
    )

    watch(
      run,
      async (currentRun, previousRun) => {
        const status = currentRun?.status
        const previousStatus = previousRun?.status

        if (!shouldHandleTerminalTransition({
          operationId: operationId.value,
          terminalHandled: state.value.terminalHandled,
          status,
          previousStatus,
        })) {
          return
        }

        if (status === 'COMPLETED' && currentRun) {
          dispatch({ type: 'PARSE_STARTED' })
          try {
            const result = await definition.parseOutput(currentRun)
            dispatch({ type: 'PARSE_SUCCEEDED', data: result })
          } catch (selectionError) {
            const nextError
              = selectionError instanceof Error
                ? selectionError
                : new Error(String(selectionError))
            dispatch({ type: 'PARSE_FAILED', error: nextError })
          }
        } else if (status) {
          dispatch({
            type: 'TERMINAL_NON_COMPLETED',
            error: new Error(`${operationKey} ${status.toLowerCase()}`),
          })
        }
      },
      { immediate: true },
    )

    const enqueueMutation = useMutation({
      mutationFn: (input: Input) =>
        enqueueWithDefinition(input, {
          idempotencyKey:
            state.value.idempotencyKey ?? createClientIdempotencyKey(),
        }),
      onError: (mutationError) => {
        const nextError
          = mutationError instanceof Error
            ? mutationError
            : new Error(String(mutationError))
        dispatch({ type: 'ENQUEUE_FAILED', error: nextError })
      },
      onSuccess(enqueueResult) {
        setTracking({ operationId: enqueueResult.operationId })
      },
    })

    watch(
      () => ({
        hasTracking: !!operationId.value,
        tokenError: context.realtimeTokenError.value,
        tokenPending: context.isRealtimeTokenPending.value,
        hasToken: !!context.realtimeAccessToken.value,
      }),
      (tokenState) => {
        const nextError = tokenAvailabilityError(tokenState)
        if (nextError) {
          dispatch({ type: 'TOKEN_UNAVAILABLE', error: nextError })
        }
      },
    )

    function beginMutation() {
      if (isActive.value) {
        return false
      }

      dispatch({
        type: 'BEGIN_MUTATION',
        idempotencyKey: createClientIdempotencyKey(),
        startedAt: new Date(),
      })
      return true
    }

    function mutate(input: Input) {
      if (!beginMutation()) {
        return
      }

      enqueueMutation.mutate(input)
    }

    function mutateAsync(input: Input): Promise<Result> {
      if (!beginMutation()) {
        return Promise.reject(new Error('Operation already in progress'))
      }

      return new Promise((resolve, reject) => {
        asyncHandlers = { resolve, reject }
        enqueueMutation.mutate(input)
      })
    }

    function reset() {
      dispatch({ type: 'RESET' })
    }

    return {
      mutate,
      mutateAsync,
      isActive,
      isDetecting: isActive,
      isSuccess: computed(
        () =>
          state.value.data !== undefined
          && !isActive.value
          && state.value.error === null,
      ),
      isError: computed(
        () => state.value.error !== null && !isActive.value,
      ),
      data: computed(() => state.value.data),
      error: computed(() => state.value.error),
      progress,
      operationId,
      reset,
    }
  }
}

export type {
  ScopedOperationScope,
} from '../utils/scoped-operation-contract'
